import React, { FC, useEffect } from 'react';
import ReactTooltip from 'react-tooltip';
import {
  currencyFormatter,
  currencyFormatterForSignedNumbers,
  currencyFormatterForSignedNumbersWithoutDecimalPoints,
  getFrequencies,
} from 'utils/commonUtil';
import infoIcon from 'images/icons/icon-info.png';
import {
  BASED_ON,
  PREMIUM,
  ANNUAL_EMPLOYER_CONTRIBUTION,
  ANNUAL_EMPLOYER_ALLOWANCE,
  ANNUAL_PREMIUM,
  ESTIMATED_MEDICAL_COST,
  OOP_MAX,
  HSA_CONTRIBUTION,
  HRA_ALLOWANCE,
  ESTIMATED_ANNUAL_COST,
  DEDUCTIBLE,
  COPAYS,
  COINSURANCE,
  YOU_PAY,
  OOP_MAX_EXCEEDED_MESSAGE,
} from 'constants/languageConstants';
import { getText } from 'utils/stringUtil';
import { useAppSelector } from 'hooks/redux';
import styles from './annualCostBreakdown.module.less';

type AnnualCostBreakdownProps = {
  activePlan: any;
};

const AnnualCostBreakdown: FC<AnnualCostBreakdownProps> = (
  props: AnnualCostBreakdownProps
) => {
  const { activePlan = {} } = props;
  const language: 'ENGLISH' | 'SPANISH' | undefined = useAppSelector(
    (state) => state.benguide.data?.language
  );
  const {
    annualPremiumConfig = {},
    estAnnualBreakdown = {},
    hsaCompatiblePlan,
    oopMaxIncluded,
    medicalCostIncluded,
    premiumIncluded,
    procedureCostIncluded,
    estimatedMedicalCostConfig = {},
    hraCompatiblePlan,
    hraPlan = {},
  } = activePlan;

  const {
    annualPremiumCost = 0,
    estMedicalCost = 0,
    estAnnualCost = 0,
    hsaContribution = 0,
    oopMaxCost = 0,
  } = estAnnualBreakdown;

  useEffect(() => {
    ReactTooltip.rebuild();
  }, [activePlan]);

  const getToolTip = (type: any) => {
    const { monthlyCost = 0.0, premiumFrequency } = annualPremiumConfig;
    let toolTipText = '<div class="info-tooltip-content-wrapper">';
    if (type !== 'estMedicalCost') {
      toolTipText += `<strong class="based-on">${getText(
        BASED_ON,
        language
      )}</strong>`;
    }
    if (type === 'annualPremiumCost') {
      toolTipText += `<div>$${monthlyCost.toFixed(2)} ${getText(
        PREMIUM,
        language
      )} <span>&#215;</span> ${getFrequencies(premiumFrequency)}<div>`;
    }
    if (type === 'hsaContribution') {
      toolTipText += `<div>${getText(
        ANNUAL_EMPLOYER_CONTRIBUTION,
        language
      )}<div>`;
    }
    if (type === 'hraContribution') {
      const allowanceText = getText(ANNUAL_EMPLOYER_ALLOWANCE, language)
        .replace(
          '{individual}',
          currencyFormatterForSignedNumbers(
            hraPlan?.employerContribution?.employerEEContribution
          )
        )
        .replace(
          '{family}',
          currencyFormatterForSignedNumbers(
            hraPlan?.employerContribution?.employerEFContribution
          )
        );
      toolTipText += `<div>${allowanceText}<div>`;
    }
    return toolTipText;
  };

  const getMedCostTooltipText = () => {
    const {
      coPayment = 0,
      deductible = 0,
      eeProcedureCost = 0,
      oopMaxCost = 0,
      estMedicalCost = 0,
    } = estAnnualBreakdown;
    const { procedureSelected } = estimatedMedicalCostConfig;

    return (
      <div>
        <div className="med-cost-tooltip-content">
          <div className="tooltip-values">
            {procedureSelected && <span>{getText(DEDUCTIBLE, language)}</span>}
            <span>{getText(COPAYS, language)}</span>
            {procedureCostIncluded && (
              <span>{getText(COINSURANCE, language)}</span>
            )}
          </div>
          <div className="tooltip-values currency-section">
            {procedureSelected && (
              <strong className="tooltip-currency-values">
                {currencyFormatterForSignedNumbers(deductible)}
              </strong>
            )}
            <strong className="tooltip-currency-values">
              {currencyFormatterForSignedNumbers(coPayment)}
            </strong>
            {procedureCostIncluded && (
              <strong className="tooltip-currency-values">
                {currencyFormatterForSignedNumbers(eeProcedureCost)}
              </strong>
            )}
          </div>
        </div>
        <div className="tooltip-plan-cost-section">
          <strong>
            {getText(YOU_PAY, language)}{' '}
            {medicalCostIncluded
              ? currencyFormatterForSignedNumbers(estMedicalCost)
              : currencyFormatterForSignedNumbers(oopMaxCost)}
            *
          </strong>
        </div>
        {oopMaxIncluded && (
          <>
            <hr />
            <div>
              <span>
                {getText(OOP_MAX_EXCEEDED_MESSAGE, language).replace(
                  '{amount}',
                  currencyFormatterForSignedNumbers(oopMaxCost)
                )}
              </span>
            </div>
          </>
        )}
      </div>
    );
  };

  const getDigitBasedStyle = (value: any) => {
    const digitCount = Math.round(value).toString().length;
    if (digitCount < 6) return styles.lowestValue;
    if (digitCount === 6) return styles.lowValue;
    if (digitCount === 7) return styles.mediumValue;
    return styles.highValue;
  };

  return (
    <div className={styles.calculatedFields}>
      <ReactTooltip
        id="med-cost-tooltip"
        className={styles.breakdownTooltip}
        place="right"
        type="light"
        effect="solid"
        border={true}
        borderColor="gray"
      >
        {getMedCostTooltipText()}
      </ReactTooltip>
      <ReactTooltip
        id="breakdown-tooltip"
        className={styles.breakdownTooltip}
        place="right"
        type="light"
        effect="solid"
        border={true}
        borderColor="gray"
        html={true}
      ></ReactTooltip>
      {premiumIncluded && (
        <div className={styles.breakdownField}>
          <div className={styles.title}>
            {getText(ANNUAL_PREMIUM, language)}
            <img
              src={infoIcon}
              alt="info"
              className={styles.infoTooltipIcon}
              data-for="breakdown-tooltip"
              data-tip={getToolTip('annualPremiumCost')}
              data-html={true}
            />
          </div>
          <div className={getDigitBasedStyle(annualPremiumCost)}>
            {currencyFormatter(Math.round(annualPremiumCost))}
          </div>
        </div>
      )}
      {medicalCostIncluded && premiumIncluded && (
        <div className={styles.sign}>+</div>
      )}
      {medicalCostIncluded && (
        <div className={styles.breakdownField}>
          <div className={styles.title}>
            {getText(ESTIMATED_MEDICAL_COST, language)}
            <img
              src={infoIcon}
              alt="info"
              className={styles.infoTooltipIcon}
              data-for="med-cost-tooltip"
              data-tip
            />
          </div>
          <div className={getDigitBasedStyle(estMedicalCost)}>
            {currencyFormatter(Math.round(estMedicalCost))}
          </div>
        </div>
      )}
      {oopMaxIncluded && (
        <React.Fragment>
          {premiumIncluded && <div className={styles.sign}>+</div>}
          <div className={styles.breakdownField}>
            <div className={styles.title}>
              {getText(OOP_MAX, language)}
              <img
                src={infoIcon}
                alt="info"
                className={styles.infoTooltipIcon}
                data-for="med-cost-tooltip"
                data-tip
              />
            </div>
            {window.innerWidth > 576 ? (
              <>
                <div className={getDigitBasedStyle(oopMaxCost)}>
                  {currencyFormatterForSignedNumbers(oopMaxCost)}
                </div>
              </>
            ) : (
              <div className={getDigitBasedStyle(oopMaxCost)}>
                {currencyFormatter(Math.round(oopMaxCost))}
              </div>
            )}
          </div>
        </React.Fragment>
      )}

      {hsaCompatiblePlan && Math.round(hsaContribution) !== 0 && (
        <>
          <div className={styles.sign}>-</div>
          <div className={styles.breakdownField}>
            <div className={styles.title}>
              {getText(HSA_CONTRIBUTION, language)}
              <img
                src={infoIcon}
                alt="info"
                className={styles.infoTooltipIcon}
                data-for="breakdown-tooltip"
                data-tip={getToolTip('hsaContribution')}
                data-html={true}
              />
            </div>
            <div
              className={`${getDigitBasedStyle(hsaContribution)} ${
                styles.greenText
              }`}
            >
              {currencyFormatter(Math.round(hsaContribution))}
            </div>
          </div>
        </>
      )}
      {hraCompatiblePlan && (
        <>
          <div className={styles.sign}>-</div>
          <div className={styles.breakdownField}>
            <div className={styles.title}>
              {getText(HRA_ALLOWANCE, language)}
              <img
                src={infoIcon}
                alt="info"
                className={styles.infoTooltipIcon}
                data-for="breakdown-tooltip"
                data-tip={getToolTip('hraContribution')}
                data-html={true}
              />
            </div>
            <div
              className={`${getDigitBasedStyle(
                activePlan.estAnnualBreakdown.hraContribution
              )} ${styles.greenText}`}
            >
              {currencyFormatter(
                Math.round(activePlan.estAnnualBreakdown.hraContribution)
              )}
            </div>
          </div>
        </>
      )}

      <div className={styles.sign}>=</div>
      <div className={`${styles.breakdownField} ${styles.total}`}>
        <div className={styles.title}>
          {getText(ESTIMATED_ANNUAL_COST, language)}
        </div>
        <div
          className={`${getDigitBasedStyle(estAnnualCost)} ${styles.greenText}`}
        >
          {currencyFormatterForSignedNumbersWithoutDecimalPoints(
            Math.round(estAnnualCost)
          )}
        </div>
      </div>
    </div>
  );
};

export default AnnualCostBreakdown;
